import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope as solidEnvelope, faQuoteLeft } from "@fortawesome/free-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import throttle from "../Components/throttle";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "../scss/_alumContact.scss";

function AlumContact() {
    const { t, i18n } = useTranslation();
    const alumDataContainer = i18n.getResourceBundle(i18n.language, "alumContactContainer");
    const alumData = alumDataContainer.testimonyTranslation;

    const [currIndex, setCurrIndex] = useState(0);

    const [width, setWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const incrementIndex = () => {
        setCurrIndex((prevIndex) => (prevIndex + 1) % alumData.length); 
    };

    const throttleIncrementIndex = throttle(incrementIndex, 100);

    const [value, setValue] = useState({
        name: "",
        email: "",
        details: "",
    });

    const [errors, setErrors] = useState({});

    const handleChange = (element) => {
        const { name, value } = element.target;
        setValue((prevValue) => ({
            ...prevValue,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const errors = {};
        if (!value.name) {
            errors.name = "Name is required";
        }
        if (!value.email) {
            errors.email = "Email is required";
        }
        if (!value.details) {
            errors.details = "Details are required";
        }
        setErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const navigate = useNavigate();
    const goToSubmissionPage = () => {
        navigate("/Submission");
    };

    function sendEmail(element) {
        element.preventDefault();
        if (!validateForm()) {
            return;
        }

        const template = {
            from_name: value.name,
            email: value.email,
            message: value.details,
            // file: value.file
        };

        emailjs
            .send(
                process.env.REACT_APP_SERVICE_ID,
                process.env.REACT_APP_ALUMCONTACT_TEMPLATE_ID,
                template,
                process.env.REACT_APP_OPTIONS
            )
            .then(() => {
                goToSubmissionPage();
                setValue({
                    name: "",
                    email: "",
                    details: "",
                    // file: null
                });
            });
    }

    const getDisplayedTestimonials = () => {
        const displayedTestimonials = alumData.map((alum) => ({
            ...alum,
            image: alum.image.startsWith("http") ? alum.image : require(`../assets/${alum.image}`)
        }));
        

        const arrangedTestimonials = [];
        for (let i = 0; i < displayedTestimonials.length; i++) {
            const index = (currIndex + i) % displayedTestimonials.length;
            arrangedTestimonials.push(displayedTestimonials[index]);
        }
        return arrangedTestimonials;
        
    };

    const displayedTestimonials = getDisplayedTestimonials();

    return (
        <div className="alumContact">
            <div className="alumContact-top">
                <div className="alumni">
                    <div className="alumni-testimonials">
                        <div className="alumni-testimonials-title">
                            <h1>{t("testimonial")}</h1>
                        </div>
                        <div className="alumni-testimonials-images">
                            <AnimatePresence>
                                {displayedTestimonials.map((alum, index) => (
                                    <div key={alum.id}>
                                        <motion.img
                                            layout
                                            transition={{
                                                layout: {
                                                    duration: 0.45,
                                                }
                                            }}
                                            className={`animation ${index === 0 ? "focus" : ""}`}
                                            src={alum.image}
                                            alt={`Alumni ${index + 1}`}
                                            style={{ display: width <= 450 ? (index < 3 ? "block" : "none") : "block" }}
                                        />
                                    </div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                    <div className="alumni-text">
                        <FontAwesomeIcon icon={faQuoteLeft} className="quote" />
                        <div className="alumni-text-testimony">
                            <h3>{alumData[currIndex].name}</h3>
                            <p className="bold">{alumData[currIndex].testimony}</p>
                            &nbsp;
                        </div>
                        <button className="next" onClick={throttleIncrementIndex}>
                            {t("nextButton")}
                        </button>
                    </div>
                </div>
            </div>
            <div className="alumContact-bottom">
                <div className="join-us">
                    <div className="join-us-text">
                        <h1>{t("joinUs")}</h1>
                        <p>{t("joinUsP")}</p>
                    </div>
                    <div className="join-us-right">
                        <form
                            className="join-us-right-form"
                            onSubmit={sendEmail}
                        >
                            <h3>{t("reachOut")}</h3>
                            <input
                                name="name"
                                type="text"
                                placeholder={t("namePlaceholder")}
                                className="input"
                                onChange={handleChange}
                            />
                            {errors.name && (
                                <p className="error">{errors.name}</p>
                            )}
                            <input
                                name="email"
                                type="email"
                                placeholder="Email"
                                className="input"
                                onChange={handleChange}
                            />
                            {errors.email && (
                                <p className="error">{errors.email}</p>
                            )}
                            <input
                                name="details"
                                placeholder={t("detailsPlaceholder")}
                                className="input details"
                                onChange={handleChange}
                            />
                            {errors.details && (
                                <p className="error">{errors.details}</p>
                            )}
                            <motion.button
                                className="join-us-right-button"
                                whileHover={{ scale: 1.05 }}
                                type="submit"
                            >
                                <p>{t("submit")}</p>
                                <FontAwesomeIcon
                                    className="envelope"
                                    icon={solidEnvelope}
                                    size="50px"
                                />
                            </motion.button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AlumContact;
